import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconCalendarEvent, IconChecklist } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Appointment, Task } from '@medplum/fhirtypes';
import { getTaskDisplayDescription } from '../../lib/portalAppointmentTaskDisplay';
import {
  buildProviderTaskNavigationUrl,
  resolveProviderTaskDestination,
} from '../../lib/providerTaskNavigation';
import { isFhirAppointmentUpcoming } from '../../lib/appointmentEffectiveEnd';
import { resolveCurrentUserPractitioner } from '../../lib/practitionerIdentity';
import { useTasks } from '../tasks/hooks/useTasks';
import { getTaskDueDate, isTaskOverdue } from '../tasks/services/taskFhirMapping';

type HomeAppointmentCategory = 'session' | 'group' | 'meeting' | 'time-entry';

interface HomeAppointment {
  id: string;
  timeLabel: string;
  title: string;
  clientName: string;
  category: HomeAppointmentCategory;
  /** Display label for the badge (e.g. "Supervision", "Vacation"). */
  badgeLabel: string;
  joinUrl: string;
}

function getDashboardDueInfo(task: Task): { label: string; color: string } {
  const dueDate = getTaskDueDate(task);
  if (!dueDate) return { label: 'No due date', color: 'gray' };
  if (isTaskOverdue(task)) return { label: 'Overdue', color: 'red' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return { label: 'Today', color: 'yellow' };
  if (diffDays === 1) return { label: 'Tomorrow', color: 'blue' };
  if (diffDays <= 7) return { label: 'This week', color: 'blue' };
  return { label: due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: 'gray' };
}

function formatTimeLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = d.getMinutes();
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

const CATEGORY_SYSTEM = 'https://unify.health/fhir/appointment-category';
const VALID_CATEGORIES = new Set<string>(['session', 'group', 'meeting', 'time-entry']);

function resolveCategory(appt: Appointment): HomeAppointmentCategory {
  const codings = appt.serviceCategory?.flatMap((sc) => sc.coding ?? []) ?? [];
  const match = codings.find(
    (c) => c.system === CATEGORY_SYSTEM && c.code && VALID_CATEGORIES.has(c.code),
  );
  return (match?.code as HomeAppointmentCategory) ?? 'session';
}

function resolveServiceTypeLabel(appt: Appointment): string | undefined {
  const coding = appt.serviceType?.[0]?.coding?.[0];
  return coding?.display?.trim() || appt.serviceType?.[0]?.text?.trim() || undefined;
}

function appointmentToHomeAppointment(appt: Appointment): HomeAppointment | null {
  if (!appt.id || !appt.start) return null;
  const patientParticipant = (appt.participant ?? []).find((p) =>
    p.actor?.reference?.startsWith('Patient/'),
  );
  const practitionerParticipant = (appt.participant ?? []).find((p) =>
    p.actor?.reference?.startsWith('Practitioner/'),
  );
  const category = resolveCategory(appt);
  const serviceLabel = resolveServiceTypeLabel(appt);
  const timeLabel = formatTimeLabel(appt.start);

  let title: string;
  let badgeLabel: string;
  let clientName: string;

  switch (category) {
    case 'meeting':
      title = `${timeLabel} — ${serviceLabel || 'Meeting'}`;
      badgeLabel = serviceLabel || 'Meeting';
      clientName = practitionerParticipant?.actor?.display ?? 'Meeting';
      break;
    case 'time-entry':
      title = `${timeLabel} — ${serviceLabel || 'Time Entry'}`;
      badgeLabel = serviceLabel || 'Time Entry';
      clientName = serviceLabel || 'Time Entry';
      break;
    case 'group':
      title = `${timeLabel} — Group Session`;
      badgeLabel = serviceLabel || 'Group';
      clientName = patientParticipant?.actor?.display ?? 'Group Session';
      break;
    default:
      clientName = patientParticipant?.actor?.display ?? 'Session';
      title = `${timeLabel} with ${clientName}`;
      badgeLabel = serviceLabel || 'Session';
      break;
  }

  return {
    id: appt.id,
    timeLabel,
    title,
    clientName,
    category,
    badgeLabel,
    joinUrl: `/dashboard/session/${appt.id}`,
  };
}

function useTodayAppointments(): { appointments: HomeAppointment[]; loading: boolean } {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [appointments, setAppointments] = useState<HomeAppointment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchToday = useCallback(async () => {
    if (!profile) {
      setLoading(false);
      return;
    }
    try {
      const practitioner = await resolveCurrentUserPractitioner(medplum, profile);
      if (!practitioner?.id) {
        setAppointments([]);
        setLoading(false);
        return;
      }
      const today = new Date();
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);

      const search = new URLSearchParams();
      search.set('actor', `Practitioner/${practitioner.id}`);
      search.append('date', `ge${startOfDay.toISOString()}`);
      search.append('date', `lt${endOfDay.toISOString()}`);
      search.set('status', 'proposed,booked,fulfilled,noshow');
      search.set('_sort', 'date');
      search.set('_count', '20');

      medplum.invalidateSearches('Appointment');
      const results = await medplum.searchResources('Appointment', search);

      const mapped = results
        .filter((appt) => isFhirAppointmentUpcoming(appt))
        .map(appointmentToHomeAppointment)
        .filter((a): a is HomeAppointment => a !== null);
      setAppointments(mapped);
    } catch {
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  useEffect(() => {
    void fetchToday();
  }, [fetchToday]);

  return { appointments, loading };
}

/**
 * Dashboard page (/dashboard).
 */
export function HomeTilesPage(): JSX.Element {
  const navigate = useNavigate();
  const profile = useMedplumProfile();
  const { appointments: todayAppointments, loading: appointmentsLoading } = useTodayAppointments();

  // Real Medplum-backed tasks for the dashboard panel
  const { tasks: allTasks, loading: tasksLoading } = useTasks();

  const currentUserRef = useMemo<string | undefined>(() => {
    if (profile?.resourceType === 'Practitioner' && profile.id) {
      return `Practitioner/${profile.id}`;
    }
    return undefined;
  }, [profile]);

  // Show active tasks assigned to the current user (max 5 for the dashboard preview)
  const dashboardTasks = useMemo(() => {
    return allTasks
      .filter((t) =>
        t.status !== 'completed' &&
        t.status !== 'cancelled' &&
        (currentUserRef == null || t.owner?.reference === currentUserRef),
      )
      .slice(0, 5);
  }, [allTasks, currentUserRef]);

  const openTaskCount = dashboardTasks.length;

  const handleDashboardTaskClick = useCallback((task: Task): void => {
    const destination = resolveProviderTaskDestination(task);
    if (destination) {
      navigate(buildProviderTaskNavigationUrl(destination));
      return;
    }
    navigate('/dashboard/tasks');
  }, [navigate]);

  const handleJoinAppointment = (appointment: HomeAppointment): void => {
    window.open(appointment.joinUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Box>
          <Title order={2} mb="xs">
            Dashboard
          </Title>
          <Text size="sm" c="gray.6">
            Quick view of what needs attention today, plus frequently used resources.
          </Text>
        </Box>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex' }}>
            <Paper p="lg" withBorder style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <IconCalendarEvent size={18} stroke={1.5} />
                  <Title order={3}>Upcoming today</Title>
                </Group>
                <Badge variant="light" color="blue">
                  {appointmentsLoading ? '…' : `${todayAppointments.length} event${todayAppointments.length === 1 ? '' : 's'}`}
                </Badge>
              </Group>

              <Stack gap="sm" style={{ flex: 1 }}>
                {appointmentsLoading ? (
                  <Text size="sm" c="dimmed">Loading appointments...</Text>
                ) : todayAppointments.length === 0 ? (
                  <Text size="sm" c="dimmed">No appointments scheduled for today.</Text>
                ) : (
                  todayAppointments.map((appt) => {
                    const badgeColor =
                      appt.category === 'meeting' ? 'violet'
                        : appt.category === 'time-entry' ? 'gray'
                          : appt.category === 'group' ? 'grape'
                            : 'blue';
                    const isSession = appt.category === 'session' || appt.category === 'group';
                    return (
                      <Paper
                        key={appt.id}
                        withBorder
                        radius="md"
                        p="sm"
                        style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
                      >
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                          <Stack gap={2} style={{ minWidth: 0 }}>
                            <Text size="sm" fw={500}>
                              {appt.title}
                            </Text>
                            <Badge variant="light" color={badgeColor} size="sm">
                              {appt.badgeLabel}
                            </Badge>
                          </Stack>

                          <Stack gap={6} align="flex-end" style={{ flexShrink: 0 }}>
                            {isSession && (
                              <Button size="xs" color="gold" variant="light" onClick={() => handleJoinAppointment(appt)}>
                                Join Session
                              </Button>
                            )}
                            <Button size="xs" variant="subtle" onClick={() => navigate(`/dashboard/calendar?eventId=${appt.id}`)}>
                              {isSession ? 'Manage Session' : 'View on Calendar'}
                            </Button>
                          </Stack>
                        </Group>
                      </Paper>
                    );
                  })
                )}
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex' }}>
            <Paper p="lg" withBorder style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <IconChecklist size={18} stroke={1.5} />
                  <Title order={3}>Tasks to complete</Title>
                </Group>
                <Badge variant="light" color={openTaskCount > 0 ? 'blue' : 'green'}>
                  {openTaskCount} open
                </Badge>
              </Group>

              <Stack gap="xs" style={{ flex: 1 }}>
                {tasksLoading ? (
                  <Text size="sm" c="dimmed">Loading tasks...</Text>
                ) : dashboardTasks.length === 0 ? (
                  <Text size="sm" c="dimmed">No tasks to complete.</Text>
                ) : (
                  dashboardTasks.map((task) => {
                    const due = getDashboardDueInfo(task);
                    const title = getTaskDisplayDescription(task, 'provider') || 'Untitled task';
                    return (
                      <Card
                        key={task.id}
                        withBorder
                        padding="sm"
                        radius="md"
                        onClick={() => handleDashboardTaskClick(task)}
                        className="dashboard-task-row"
                        style={{
                          cursor: 'pointer',
                          transition: 'box-shadow 150ms ease',
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                          <Text size="sm" fw={600} lineClamp={2} style={{ minWidth: 0 }}>
                            {title}
                          </Text>
                          <Badge size="sm" variant="light" color={due.color} style={{ flexShrink: 0 }}>
                            {due.label}
                          </Badge>
                        </Group>
                      </Card>
                    );
                  })
                )}
              </Stack>

              <style>{`.dashboard-task-row:hover { box-shadow: var(--mantine-shadow-sm); }`}</style>

              <Group justify="flex-end" mt="md">
                <Button size="xs" variant="light" onClick={() => navigate('/dashboard/tasks')}>
                  View all tasks
                </Button>
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
}
